import { useEffect, useState, useMemo } from 'react';
import { useParams, Link, useSearchParams, useNavigate } from 'react-router-dom';
import axios from 'axios';
import { Helmet } from 'react-helmet-async';
import { Layout } from '@/components/Layout';
import { 
  ArrowLeft, Loader2, TrendingUp, TrendingDown, 
  Calendar, Target, CheckCircle, XCircle, Clock,
  Edit2, Save, X, ChevronDown, BarChart3, LineChart, Activity, Flame, Zap,
  GitCompare, UserPlus, Search
} from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Textarea } from '@/components/ui/textarea';
import { useAuth } from '@/contexts/AuthContext';
import { toast } from 'sonner';
import {
  LineChart as RechartsLineChart,
  Line,
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
  Cell,
  Legend
} from 'recharts';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

// Color palette for comparison charts
const COMPARISON_COLORS = ['#34C759', '#FF9500', '#5856D6', '#FF3B30'];

export default function PlayerDetail() {
  const { slug } = useParams();
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const { isAuthenticated, token } = useAuth();
  const [player, setPlayer] = useState(null);
  const [loading, setLoading] = useState(true);
  const [editing, setEditing] = useState(false);
  const [customContent, setCustomContent] = useState('');
  const [selectedChart, setSelectedChart] = useState('hitRate');
  const [dropdownOpen, setDropdownOpen] = useState(false);
  
  // Compare feature state
  const [comparePlayers, setComparePlayers] = useState([]);
  const [compareLoading, setCompareLoading] = useState(false);
  const [showCompareSearch, setShowCompareSearch] = useState(false);
  const [allPlayers, setAllPlayers] = useState([]);
  const [searchQuery, setSearchQuery] = useState('');

  // Chart options for dropdown
  const chartOptions = [
    { id: 'hitRate', label: 'Hit Rate Trend', icon: LineChart },
    { id: 'propType', label: 'Performance by Prop Type', icon: BarChart3 },
    { id: 'timeline', label: 'Results Timeline', icon: Activity },
  ];

  // Update document title when player data is available
  useEffect(() => {
    if (player && player.name) {
      document.title = player.name + ' Prop Bet Analysis & Picks | PrimeStatLab';
    } else {
      document.title = 'Player Analysis | PrimeStatLab';
    }
    return () => {
      document.title = 'PrimeStatLab';
    };
  }, [player]);

  useEffect(() => {
    fetchPlayer();
  }, [slug]);

  // Fetch comparison players from URL params
  useEffect(() => {
    const compareParam = searchParams.get('compare');
    if (compareParam && player) {
      const compareSlugs = compareParam.split(',').filter(s => s);
      fetchComparePlayers(compareSlugs);
    }
  }, [searchParams, player]);

  // Fetch all players for search dropdown
  useEffect(() => {
    const fetchAllPlayers = async () => {
      try {
        const response = await axios.get(`${API}/players`);
        setAllPlayers(response.data);
      } catch (error) {
        console.error('Failed to fetch all players:', error);
      }
    };
    fetchAllPlayers();
  }, []);

  const fetchPlayer = async () => {
    try {
      const response = await axios.get(`${API}/players/${slug}`);
      setPlayer(response.data);
      setCustomContent(response.data.custom_content || '');
    } catch (error) {
      console.error('Failed to fetch player:', error);
    } finally {
      setLoading(false);
    }
  };

  const fetchComparePlayers = async (slugs) => {
    setCompareLoading(true);
    try {
      const promises = slugs.map(s => axios.get(`${API}/players/${s}`));
      const responses = await Promise.all(promises);
      setComparePlayers(responses.map(r => r.data));
    } catch (error) {
      console.error('Failed to fetch comparison players:', error);
      toast.error('Failed to load comparison players');
    } finally {
      setCompareLoading(false);
    }
  };

  const addComparePlayer = (playerName) => {
    const newSlug = playerName.toLowerCase().replace(/\s+/g, '-').replace(/[^a-z0-9-]/g, '');
    const currentCompare = searchParams.get('compare') || '';
    const currentSlugs = currentCompare ? currentCompare.split(',') : [];
    
    if (currentSlugs.length < 2 && !currentSlugs.includes(newSlug) && newSlug !== slug) {
      const newCompare = [...currentSlugs, newSlug].join(',');
      navigate(`/players/${slug}?compare=${newCompare}`);
    }
    setShowCompareSearch(false);
    setSearchQuery('');
  };

  const removeComparePlayer = (playerSlug) => {
    const currentCompare = searchParams.get('compare') || '';
    const currentSlugs = currentCompare.split(',').filter(s => s !== playerSlug);
    
    if (currentSlugs.length > 0) {
      navigate(`/players/${slug}?compare=${currentSlugs.join(',')}`);
    } else {
      navigate(`/players/${slug}`);
    }
    setComparePlayers(comparePlayers.filter(p => {
      const pSlug = p.name.toLowerCase().replace(/\s+/g, '-').replace(/[^a-z0-9-]/g, '');
      return pSlug !== playerSlug;
    }));
  };

  const clearComparison = () => {
    navigate(`/players/${slug}`);
    setComparePlayers([]);
  };

  const filteredPlayers = allPlayers.filter(p => {
    const pSlug = p.name.toLowerCase().replace(/\s+/g, '-').replace(/[^a-z0-9-]/g, '');
    const isCurrentPlayer = pSlug === slug;
    const isAlreadyComparing = comparePlayers.some(cp => {
      const cpSlug = cp.name.toLowerCase().replace(/\s+/g, '-').replace(/[^a-z0-9-]/g, '');
      return cpSlug === pSlug;
    });
    const matchesSearch = p.name.toLowerCase().includes(searchQuery.toLowerCase());
    return !isCurrentPlayer && !isAlreadyComparing && matchesSearch;
  });

  const saveCustomContent = async () => {
    try {
      await axios.put(
        `${API}/players/${slug}/content`,
        { content: customContent },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      setPlayer({ ...player, custom_content: customContent });
      setEditing(false);
      toast.success('Content saved!');
    } catch (error) {
      toast.error('Failed to save content');
    }
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return '';
    const date = new Date(dateStr);
    return date.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
  };

  const getOutcomeIcon = (outcome) => {
    if (outcome === 'WIN') return <CheckCircle className="text-green-500" size={18} />;
    if (outcome === 'LOSS') return <XCircle className="text-red-500" size={18} />;
    return <Clock className="text-yellow-500" size={18} />;
  };

  // Safe calculations
  const winRate = player && (player.wins + player.losses > 0)
    ? Math.round((player.wins / (player.wins + player.losses)) * 100) 
    : 0;

  // Compute additional stats - must be called before any returns
  const computedStats = useMemo(() => {
    if (!player?.picks || player.picks.length === 0) {
      return { streak: 0, streakType: null, bestPropType: null, avgLineGap: 0 };
    }

    const completedPicks = player.picks.filter(p => 
      p.outcome && ['WIN', 'LOSS', 'PUSH'].includes(p.outcome.toUpperCase())
    );

    // Calculate current streak
    let streak = 0;
    let streakType = null;
    for (const pick of completedPicks) {
      const outcome = pick.outcome?.toUpperCase();
      if (streak === 0) {
        streakType = outcome;
        streak = 1;
      } else if (outcome === streakType) {
        streak++;
      } else {
        break;
      }
    }

    // Calculate best prop type
    const propTypeStats = {};
    completedPicks.forEach(pick => {
      const propType = pick.prop_type || 'Unknown';
      if (!propTypeStats[propType]) {
        propTypeStats[propType] = { wins: 0, total: 0 };
      }
      propTypeStats[propType].total++;
      if (pick.outcome?.toUpperCase() === 'WIN') {
        propTypeStats[propType].wins++;
      }
    });

    let bestPropType = null;
    let bestWinRate = 0;
    Object.entries(propTypeStats).forEach(([propType, stats]) => {
      if (stats.total >= 2) { // Only consider prop types with 2+ picks
        const rate = (stats.wins / stats.total) * 100;
        if (rate > bestWinRate) {
          bestWinRate = rate;
          bestPropType = { name: propType, rate: Math.round(rate), total: stats.total };
        }
      }
    });

    // Calculate average line gap (difference between actual and line)
    let totalGap = 0;
    let gapCount = 0;
    completedPicks.forEach(pick => {
      if (pick.actual_stat !== null && pick.actual_stat !== undefined && pick.line) {
        const line = parseFloat(pick.line);
        if (!isNaN(line)) {
          totalGap += pick.actual_stat - line;
          gapCount++;
        }
      }
    });
    const avgLineGap = gapCount > 0 ? (totalGap / gapCount).toFixed(1) : 0;

    return { streak, streakType, bestPropType, avgLineGap };
  }, [player]);

  // Chart data calculations - must be called before any returns
  const chartData = useMemo(() => {
    if (!player?.picks || player.picks.length === 0) {
      return { hitRateData: [], propTypeData: [], timelineData: [] };
    }

    const completedPicks = player.picks
      .filter(p => p.outcome && ['WIN', 'LOSS', 'PUSH'].includes(p.outcome.toUpperCase()))
      .reverse(); // Oldest first for timeline

    // Hit Rate Trend (rolling win rate over last N picks)
    const hitRateData = [];
    let runningWins = 0;
    completedPicks.forEach((pick, idx) => {
      if (pick.outcome?.toUpperCase() === 'WIN') runningWins++;
      const rate = Math.round((runningWins / (idx + 1)) * 100);
      hitRateData.push({
        pick: idx + 1,
        rate,
        date: new Date(pick.pick_date || pick.created_at).toLocaleDateString('en-US', { month: 'short', day: 'numeric' }),
        outcome: pick.outcome?.toUpperCase()
      });
    });

    // Performance by Prop Type
    const propTypeMap = {};
    completedPicks.forEach(pick => {
      const propType = pick.prop_type || 'Unknown';
      // Shorten long prop types
      const shortName = propType.length > 15 ? propType.split(' ').map(w => w[0]).join('+') : propType;
      if (!propTypeMap[shortName]) {
        propTypeMap[shortName] = { name: shortName, fullName: propType, wins: 0, losses: 0, total: 0 };
      }
      propTypeMap[shortName].total++;
      if (pick.outcome?.toUpperCase() === 'WIN') {
        propTypeMap[shortName].wins++;
      } else {
        propTypeMap[shortName].losses++;
      }
    });
    const propTypeData = Object.values(propTypeMap).map(pt => ({
      ...pt,
      winRate: pt.total > 0 ? Math.round((pt.wins / pt.total) * 100) : 0
    }));

    // Results Timeline
    const timelineData = completedPicks.slice(-20).map((pick, idx) => ({
      pick: idx + 1,
      outcome: pick.outcome?.toUpperCase(),
      date: new Date(pick.pick_date || pick.created_at).toLocaleDateString('en-US', { month: 'short', day: 'numeric' }),
      propType: pick.prop_type,
      value: pick.outcome?.toUpperCase() === 'WIN' ? 1 : pick.outcome?.toUpperCase() === 'LOSS' ? -1 : 0
    }));

    return { hitRateData, propTypeData, timelineData };
  }, [player]);

  // Render loading state
  if (loading) {
    return (
      <Layout>
        <div className="flex items-center justify-center py-40">
          <Loader2 className="animate-spin text-primary" size={40} />
        </div>
      </Layout>
    );
  }

  // Render not found state
  if (!player) {
    return (
      <Layout>
        <div className="container mx-auto px-6 py-20 text-center">
          <h1 className="text-3xl font-heading mb-4">Player Not Found</h1>
          <Link to="/players" className="text-primary hover:underline">
            ← Back to Players
          </Link>
        </div>
      </Layout>
    );
  }

  return (
    <Layout>
      {/* SEO Meta Tags */}
      <Helmet>
        <title>{`${player.name} Props & Analysis | NBA Player Prop Picks | PrimeStatLab`}</title>
        <meta name="description" content={`${player.name} player prop analysis and betting picks. ${player.total_picks} picks tracked with ${winRate}% win rate. Free NBA props for PrizePicks & Underdog.`} />
        <meta name="keywords" content={`${player.name} props, ${player.name} over under, ${player.name} player props, ${player.name} PrizePicks, NBA props`} />
        <link rel="canonical" href={`https://primestatlab.com/players/${slug}`} />
        <meta property="og:title" content={`${player.name} Props & Analysis | PrimeStatLab`} />
        <meta property="og:description" content={`${player.name} player prop picks and analysis. Track record: ${player.wins}W-${player.losses}L (${winRate}%).`} />
        <meta property="og:url" content={`https://primestatlab.com/players/${slug}`} />
      </Helmet>

      <div className="container mx-auto px-6 md:px-12 py-12">
        {/* Back Link */}
        <Link 
          to="/players" 
          className="inline-flex items-center text-muted-foreground hover:text-primary mb-8 transition-colors"
        >
          <ArrowLeft size={16} className="mr-2" />
          Back to All Players
        </Link>

        {/* Player Header */}
        <div className="mb-12">
          <div className="flex flex-col sm:flex-row sm:items-start sm:justify-between gap-4">
            <div>
              <h1 className="text-4xl md:text-5xl font-heading font-bold mb-4">
                {player.name}
              </h1>
              <p className="text-lg text-muted-foreground">
                Comprehensive prop betting analysis and historical picks
              </p>
            </div>
            
            {/* Compare Button */}
            <div className="relative flex-shrink-0">
              {comparePlayers.length < 2 && (
                <Button
                  onClick={() => setShowCompareSearch(!showCompareSearch)}
                  variant="outline"
                  className="flex items-center gap-2"
                  data-testid="compare-with-btn"
                >
                  <GitCompare size={18} />
                  Compare with...
                </Button>
              )}
              
              {/* Search Dropdown */}
              {showCompareSearch && (
                <div className="absolute top-full right-0 mt-2 w-72 bg-card border border-border rounded-lg shadow-xl z-20">
                  <div className="p-3 border-b border-border">
                    <div className="relative">
                      <Search size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground" />
                      <input
                        type="text"
                        placeholder="Search players..."
                        value={searchQuery}
                        onChange={(e) => setSearchQuery(e.target.value)}
                        className="w-full pl-9 pr-3 py-2 bg-surface border border-border rounded text-sm focus:outline-none focus:border-primary"
                        autoFocus
                        data-testid="compare-search-input"
                      />
                    </div>
                  </div>
                  <div className="max-h-48 overflow-y-auto">
                    {filteredPlayers.length > 0 ? (
                      filteredPlayers.slice(0, 8).map((p) => (
                        <button
                          key={p.name}
                          onClick={() => addComparePlayer(p.name)}
                          className="w-full px-4 py-2 text-left hover:bg-surface transition-colors flex items-center justify-between text-sm"
                        >
                          <span>{p.name}</span>
                          <span className={`text-xs ${
                            p.wins + p.losses > 0 
                              ? (p.wins / (p.wins + p.losses)) >= 0.5 
                                ? 'text-green-500' 
                                : 'text-red-500'
                              : 'text-muted-foreground'
                          }`}>
                            {p.wins + p.losses > 0 
                              ? `${Math.round((p.wins / (p.wins + p.losses)) * 100)}%`
                              : '-'}
                          </span>
                        </button>
                      ))
                    ) : (
                      <div className="px-4 py-3 text-sm text-muted-foreground text-center">
                        No players found
                      </div>
                    )}
                  </div>
                </div>
              )}
            </div>
          </div>
          
          {/* Comparison Pills */}
          {comparePlayers.length > 0 && (
            <div className="mt-4 flex flex-wrap items-center gap-2" data-testid="comparison-pills">
              <span className="text-sm text-muted-foreground">Comparing with:</span>
              {comparePlayers.map((cp, idx) => {
                const cpSlug = cp.name.toLowerCase().replace(/\s+/g, '-').replace(/[^a-z0-9-]/g, '');
                return (
                  <span 
                    key={cp.name}
                    className="inline-flex items-center gap-1 px-3 py-1 rounded-full text-sm"
                    style={{ 
                      backgroundColor: `${COMPARISON_COLORS[idx + 1]}20`,
                      color: COMPARISON_COLORS[idx + 1],
                      border: `1px solid ${COMPARISON_COLORS[idx + 1]}40`
                    }}
                  >
                    {cp.name}
                    <button
                      onClick={() => removeComparePlayer(cpSlug)}
                      className="hover:opacity-70"
                    >
                      <X size={14} />
                    </button>
                  </span>
                );
              })}
              {comparePlayers.length > 0 && (
                <button
                  onClick={clearComparison}
                  className="text-xs text-muted-foreground hover:text-foreground ml-2"
                >
                  Clear all
                </button>
              )}
            </div>
          )}
        </div>

        {/* Stats Cards - Enhanced */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-6">
          <div className="bg-card border border-border rounded-lg p-4 text-center">
            <div className="text-3xl font-bold text-primary">{player.total_picks}</div>
            <div className="text-sm text-muted-foreground">Total Picks</div>
          </div>
          <div className="bg-card border border-border rounded-lg p-4 text-center">
            <div className="text-3xl font-bold text-green-500">{player.wins}</div>
            <div className="text-sm text-muted-foreground">Wins</div>
          </div>
          <div className="bg-card border border-border rounded-lg p-4 text-center">
            <div className="text-3xl font-bold text-red-500">{player.losses}</div>
            <div className="text-sm text-muted-foreground">Losses</div>
          </div>
          <div className="bg-card border border-border rounded-lg p-4 text-center">
            <div className={`text-3xl font-bold ${winRate >= 50 ? 'text-green-500' : 'text-red-500'}`}>
              {winRate}%
            </div>
            <div className="text-sm text-muted-foreground">Win Rate</div>
          </div>
        </div>

        {/* Additional Stats Row */}
        <div className="grid grid-cols-3 gap-4 mb-12">
          {/* Current Streak */}
          <div className="bg-card border border-border rounded-lg p-4" data-testid="streak-card">
            <div className="flex items-center gap-2 mb-2">
              <Flame className={computedStats.streakType === 'WIN' ? 'text-green-500' : computedStats.streakType === 'LOSS' ? 'text-red-500' : 'text-muted-foreground'} size={18} />
              <span className="text-xs text-muted-foreground uppercase tracking-wider">Current Streak</span>
            </div>
            <div className="flex items-baseline gap-2">
              <span className={`text-2xl font-bold ${
                computedStats.streakType === 'WIN' ? 'text-green-500' : 
                computedStats.streakType === 'LOSS' ? 'text-red-500' : 'text-muted-foreground'
              }`}>
                {computedStats.streak > 0 ? `${computedStats.streak}${computedStats.streakType === 'WIN' ? 'W' : 'L'}` : '-'}
              </span>
              {computedStats.streak >= 3 && computedStats.streakType === 'WIN' && (
                <span className="text-xs bg-green-500/20 text-green-400 px-2 py-0.5 rounded">Hot</span>
              )}
            </div>
          </div>

          {/* Best Prop Type */}
          <div className="bg-card border border-border rounded-lg p-4" data-testid="best-prop-card">
            <div className="flex items-center gap-2 mb-2">
              <Zap className="text-primary" size={18} />
              <span className="text-xs text-muted-foreground uppercase tracking-wider">Best Prop Type</span>
            </div>
            {computedStats.bestPropType ? (
              <div>
                <div className="text-lg font-bold text-foreground truncate">{computedStats.bestPropType.name}</div>
                <div className="text-sm text-primary">{computedStats.bestPropType.rate}% ({computedStats.bestPropType.total} picks)</div>
              </div>
            ) : (
              <div className="text-muted-foreground">-</div>
            )}
          </div>

          {/* Avg Line Gap */}
          <div className="bg-card border border-border rounded-lg p-4" data-testid="line-gap-card">
            <div className="flex items-center gap-2 mb-2">
              {parseFloat(computedStats.avgLineGap) >= 0 ? (
                <TrendingUp className="text-green-500" size={18} />
              ) : (
                <TrendingDown className="text-red-500" size={18} />
              )}
              <span className="text-xs text-muted-foreground uppercase tracking-wider">Avg vs Line</span>
            </div>
            <div className={`text-2xl font-bold font-mono ${
              parseFloat(computedStats.avgLineGap) >= 0 ? 'text-green-500' : 'text-red-500'
            }`}>
              {parseFloat(computedStats.avgLineGap) >= 0 ? '+' : ''}{computedStats.avgLineGap}
            </div>
          </div>
        </div>

        {/* Trend Graphs Section */}
        {(chartData.hitRateData.length > 0 || chartData.propTypeData.length > 0) && (
          <div className="bg-card border border-border rounded-lg p-6 mb-12" data-testid="trend-graphs-section">
            {/* Header with Dropdown */}
            <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4 mb-6">
              <h2 className="text-xl font-heading font-bold flex items-center gap-2">
                <BarChart3 className="text-primary" size={22} />
                Trend Analysis
              </h2>
              
              {/* Dropdown Selector */}
              <div className="relative">
                <button
                  onClick={() => setDropdownOpen(!dropdownOpen)}
                  className="flex items-center gap-2 px-4 py-2 bg-surface border border-border rounded-lg hover:border-primary transition-colors min-w-[220px] justify-between"
                  data-testid="chart-dropdown"
                >
                  <span className="flex items-center gap-2">
                    {(() => {
                      const selected = chartOptions.find(opt => opt.id === selectedChart);
                      const Icon = selected?.icon || BarChart3;
                      return (
                        <>
                          <Icon size={16} className="text-primary" />
                          <span className="text-sm">{selected?.label || 'Select View'}</span>
                        </>
                      );
                    })()}
                  </span>
                  <ChevronDown size={16} className={`text-muted-foreground transition-transform ${dropdownOpen ? 'rotate-180' : ''}`} />
                </button>
                
                {dropdownOpen && (
                  <div className="absolute top-full left-0 right-0 mt-1 bg-card border border-border rounded-lg shadow-xl z-10 overflow-hidden">
                    {chartOptions.map((option) => {
                      const Icon = option.icon;
                      return (
                        <button
                          key={option.id}
                          onClick={() => {
                            setSelectedChart(option.id);
                            setDropdownOpen(false);
                          }}
                          className={`w-full flex items-center gap-2 px-4 py-3 hover:bg-surface transition-colors ${
                            selectedChart === option.id ? 'bg-primary/10 text-primary' : 'text-foreground'
                          }`}
                        >
                          <Icon size={16} />
                          <span className="text-sm">{option.label}</span>
                        </button>
                      );
                    })}
                  </div>
                )}
              </div>
            </div>

            {/* Chart Content */}
            <div className="h-[300px]">
              {/* Hit Rate Trend Line */}
              {selectedChart === 'hitRate' && chartData.hitRateData.length > 0 && (
                <ResponsiveContainer width="100%" height="100%">
                  <RechartsLineChart data={chartData.hitRateData} margin={{ top: 5, right: 20, left: 0, bottom: 5 }}>
                    <CartesianGrid strokeDasharray="3 3" stroke="#27272A" />
                    <XAxis 
                      dataKey="pick" 
                      tick={{ fill: '#71717A', fontSize: 12 }} 
                      axisLine={{ stroke: '#27272A' }}
                      label={{ value: 'Pick #', position: 'insideBottom', offset: -5, fill: '#71717A', fontSize: 11 }}
                    />
                    <YAxis 
                      domain={[0, 100]} 
                      tick={{ fill: '#71717A', fontSize: 12 }} 
                      axisLine={{ stroke: '#27272A' }}
                      tickFormatter={(v) => `${v}%`}
                    />
                    <Tooltip 
                      contentStyle={{ backgroundColor: '#121212', border: '1px solid #27272A', borderRadius: '8px' }}
                      labelStyle={{ color: '#FFFFFF' }}
                      formatter={(value) => [`${value}%`, 'Win Rate']}
                      labelFormatter={(label, payload) => payload?.[0]?.payload?.date || `Pick ${label}`}
                    />
                    <Line 
                      type="monotone" 
                      dataKey="rate" 
                      stroke="#34C759" 
                      strokeWidth={2}
                      dot={{ fill: '#34C759', strokeWidth: 0, r: 4 }}
                      activeDot={{ r: 6, fill: '#34C759' }}
                    />
                    {/* 50% reference line */}
                    <Line 
                      type="monotone" 
                      dataKey={() => 50} 
                      stroke="#FF3B30" 
                      strokeDasharray="5 5" 
                      strokeWidth={1}
                      dot={false}
                      activeDot={false}
                    />
                  </RechartsLineChart>
                </ResponsiveContainer>
              )}

              {/* Performance by Prop Type */}
              {selectedChart === 'propType' && chartData.propTypeData.length > 0 && (
                <ResponsiveContainer width="100%" height="100%">
                  <BarChart data={chartData.propTypeData} margin={{ top: 5, right: 20, left: 0, bottom: 30 }}>
                    <CartesianGrid strokeDasharray="3 3" stroke="#27272A" vertical={false} />
                    <XAxis 
                      dataKey="name" 
                      tick={{ fill: '#71717A', fontSize: 11 }} 
                      axisLine={{ stroke: '#27272A' }}
                      angle={-45}
                      textAnchor="end"
                      height={60}
                    />
                    <YAxis 
                      domain={[0, 100]} 
                      tick={{ fill: '#71717A', fontSize: 12 }} 
                      axisLine={{ stroke: '#27272A' }}
                      tickFormatter={(v) => `${v}%`}
                    />
                    <Tooltip 
                      contentStyle={{ backgroundColor: '#121212', border: '1px solid #27272A', borderRadius: '8px' }}
                      labelStyle={{ color: '#FFFFFF' }}
                      formatter={(value, name, props) => [
                        `${value}% (${props.payload.wins}W - ${props.payload.losses}L)`,
                        props.payload.fullName || 'Win Rate'
                      ]}
                    />
                    <Bar dataKey="winRate" radius={[4, 4, 0, 0]}>
                      {chartData.propTypeData.map((entry, index) => (
                        <Cell 
                          key={`cell-${index}`} 
                          fill={entry.winRate >= 50 ? '#34C759' : '#FF3B30'} 
                        />
                      ))}
                    </Bar>
                  </BarChart>
                </ResponsiveContainer>
              )}

              {/* Results Timeline */}
              {selectedChart === 'timeline' && chartData.timelineData.length > 0 && (
                <div className="h-full flex flex-col">
                  <p className="text-xs text-muted-foreground mb-4">Last {chartData.timelineData.length} completed picks</p>
                  <div className="flex-1 flex items-center justify-center">
                    <div className="flex gap-1 flex-wrap justify-center">
                      {chartData.timelineData.map((item, idx) => (
                        <div
                          key={idx}
                          className="group relative"
                        >
                          <div
                            className={`w-8 h-8 rounded flex items-center justify-center text-xs font-bold transition-transform hover:scale-110 cursor-pointer ${
                              item.outcome === 'WIN' 
                                ? 'bg-green-500/20 text-green-500 border border-green-500/30' 
                                : item.outcome === 'LOSS'
                                ? 'bg-red-500/20 text-red-500 border border-red-500/30'
                                : 'bg-yellow-500/20 text-yellow-500 border border-yellow-500/30'
                            }`}
                          >
                            {item.outcome === 'WIN' ? 'W' : item.outcome === 'LOSS' ? 'L' : 'P'}
                          </div>
                          {/* Tooltip on hover */}
                          <div className="absolute bottom-full left-1/2 -translate-x-1/2 mb-2 px-2 py-1 bg-card border border-border rounded text-xs whitespace-nowrap opacity-0 group-hover:opacity-100 transition-opacity pointer-events-none z-10">
                            <div className="font-bold">{item.date}</div>
                            <div className="text-muted-foreground">{item.propType}</div>
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>
                  {/* Legend */}
                  <div className="flex justify-center gap-4 mt-4 text-xs text-muted-foreground">
                    <span className="flex items-center gap-1"><span className="w-3 h-3 bg-green-500/20 border border-green-500/30 rounded"></span> Win</span>
                    <span className="flex items-center gap-1"><span className="w-3 h-3 bg-red-500/20 border border-red-500/30 rounded"></span> Loss</span>
                    <span className="flex items-center gap-1"><span className="w-3 h-3 bg-yellow-500/20 border border-yellow-500/30 rounded"></span> Push</span>
                  </div>
                </div>
              )}

              {/* Empty State */}
              {((selectedChart === 'hitRate' && chartData.hitRateData.length === 0) ||
                (selectedChart === 'propType' && chartData.propTypeData.length === 0) ||
                (selectedChart === 'timeline' && chartData.timelineData.length === 0)) && (
                <div className="h-full flex items-center justify-center text-muted-foreground">
                  <div className="text-center">
                    <BarChart3 size={40} className="mx-auto mb-2 opacity-50" />
                    <p>No completed picks to display</p>
                  </div>
                </div>
              )}
            </div>
          </div>
        )}

        {/* Player Comparison Section */}
        {comparePlayers.length > 0 && (
          <div className="bg-card border border-border rounded-lg p-6 mb-12" data-testid="comparison-section">
            <div className="flex items-center justify-between mb-6">
              <h2 className="text-xl font-heading font-bold flex items-center gap-2">
                <GitCompare className="text-primary" size={22} />
                Player Comparison
              </h2>
              <div className="flex items-center gap-2">
                {/* Color Legend */}
                <div className="flex items-center gap-3 text-xs">
                  <span className="flex items-center gap-1">
                    <span className="w-3 h-3 rounded" style={{ backgroundColor: COMPARISON_COLORS[0] }}></span>
                    {player.name}
                  </span>
                  {comparePlayers.map((cp, idx) => (
                    <span key={cp.name} className="flex items-center gap-1">
                      <span className="w-3 h-3 rounded" style={{ backgroundColor: COMPARISON_COLORS[idx + 1] }}></span>
                      {cp.name}
                    </span>
                  ))}
                </div>
              </div>
            </div>

            {compareLoading ? (
              <div className="flex items-center justify-center py-12">
                <Loader2 className="animate-spin text-primary" size={30} />
              </div>
            ) : (
              <div className="space-y-8">
                {/* Stats Comparison Table */}
                <div>
                  <h3 className="text-sm font-bold text-muted-foreground uppercase tracking-wider mb-4">Stats Overview</h3>
                  <div className="overflow-x-auto">
                    <table className="w-full text-sm">
                      <thead>
                        <tr className="border-b border-border">
                          <th className="text-left py-2 px-3 text-xs text-muted-foreground uppercase">Metric</th>
                          <th className="text-center py-2 px-3 text-xs" style={{ color: COMPARISON_COLORS[0] }}>{player.name}</th>
                          {comparePlayers.map((cp, idx) => (
                            <th key={cp.name} className="text-center py-2 px-3 text-xs" style={{ color: COMPARISON_COLORS[idx + 1] }}>{cp.name}</th>
                          ))}
                        </tr>
                      </thead>
                      <tbody>
                        <tr className="border-b border-border/50">
                          <td className="py-3 px-3 text-muted-foreground">Total Picks</td>
                          <td className="py-3 px-3 text-center font-bold">{player.total_picks}</td>
                          {comparePlayers.map((cp) => (
                            <td key={cp.name} className="py-3 px-3 text-center font-bold">{cp.total_picks}</td>
                          ))}
                        </tr>
                        <tr className="border-b border-border/50">
                          <td className="py-3 px-3 text-muted-foreground">Win Rate</td>
                          <td className="py-3 px-3 text-center">
                            <span className={`font-bold ${winRate >= 50 ? 'text-green-500' : 'text-red-500'}`}>{winRate}%</span>
                          </td>
                          {comparePlayers.map((cp) => {
                            const cpWinRate = cp.wins + cp.losses > 0 ? Math.round((cp.wins / (cp.wins + cp.losses)) * 100) : 0;
                            return (
                              <td key={cp.name} className="py-3 px-3 text-center">
                                <span className={`font-bold ${cpWinRate >= 50 ? 'text-green-500' : 'text-red-500'}`}>{cpWinRate}%</span>
                              </td>
                            );
                          })}
                        </tr>
                        <tr className="border-b border-border/50">
                          <td className="py-3 px-3 text-muted-foreground">Record (W-L)</td>
                          <td className="py-3 px-3 text-center">
                            <span className="text-green-500">{player.wins}</span>-<span className="text-red-500">{player.losses}</span>
                          </td>
                          {comparePlayers.map((cp) => (
                            <td key={cp.name} className="py-3 px-3 text-center">
                              <span className="text-green-500">{cp.wins}</span>-<span className="text-red-500">{cp.losses}</span>
                            </td>
                          ))}
                        </tr>
                        <tr className="border-b border-border/50">
                          <td className="py-3 px-3 text-muted-foreground">Current Streak</td>
                          <td className="py-3 px-3 text-center">
                            <span className={computedStats.streakType === 'WIN' ? 'text-green-500' : computedStats.streakType === 'LOSS' ? 'text-red-500' : 'text-muted-foreground'}>
                              {computedStats.streak > 0 ? `${computedStats.streak}${computedStats.streakType === 'WIN' ? 'W' : 'L'}` : '-'}
                            </span>
                          </td>
                          {comparePlayers.map((cp) => {
                            // Calculate streak for compare player
                            const cpPicks = cp.picks?.filter(p => p.outcome && ['WIN', 'LOSS', 'PUSH'].includes(p.outcome.toUpperCase())) || [];
                            let cpStreak = 0;
                            let cpStreakType = null;
                            for (const pick of cpPicks) {
                              const outcome = pick.outcome?.toUpperCase();
                              if (cpStreak === 0) { cpStreakType = outcome; cpStreak = 1; }
                              else if (outcome === cpStreakType) { cpStreak++; }
                              else { break; }
                            }
                            return (
                              <td key={cp.name} className="py-3 px-3 text-center">
                                <span className={cpStreakType === 'WIN' ? 'text-green-500' : cpStreakType === 'LOSS' ? 'text-red-500' : 'text-muted-foreground'}>
                                  {cpStreak > 0 ? `${cpStreak}${cpStreakType === 'WIN' ? 'W' : 'L'}` : '-'}
                                </span>
                              </td>
                            );
                          })}
                        </tr>
                        <tr>
                          <td className="py-3 px-3 text-muted-foreground">Avg vs Line</td>
                          <td className="py-3 px-3 text-center">
                            <span className={`font-mono ${parseFloat(computedStats.avgLineGap) >= 0 ? 'text-green-500' : 'text-red-500'}`}>
                              {parseFloat(computedStats.avgLineGap) >= 0 ? '+' : ''}{computedStats.avgLineGap}
                            </span>
                          </td>
                          {comparePlayers.map((cp) => {
                            // Calculate avg line gap for compare player
                            const cpPicks = cp.picks?.filter(p => p.outcome && ['WIN', 'LOSS', 'PUSH'].includes(p.outcome.toUpperCase())) || [];
                            let totalGap = 0;
                            let gapCount = 0;
                            cpPicks.forEach(pick => {
                              if (pick.actual_stat !== null && pick.actual_stat !== undefined && pick.line) {
                                const line = parseFloat(pick.line);
                                if (!isNaN(line)) {
                                  totalGap += pick.actual_stat - line;
                                  gapCount++;
                                }
                              }
                            });
                            const cpAvgLineGap = gapCount > 0 ? (totalGap / gapCount).toFixed(1) : 0;
                            return (
                              <td key={cp.name} className="py-3 px-3 text-center">
                                <span className={`font-mono ${parseFloat(cpAvgLineGap) >= 0 ? 'text-green-500' : 'text-red-500'}`}>
                                  {parseFloat(cpAvgLineGap) >= 0 ? '+' : ''}{cpAvgLineGap}
                                </span>
                              </td>
                            );
                          })}
                        </tr>
                      </tbody>
                    </table>
                  </div>
                </div>

                {/* Win Rate Comparison Chart */}
                <div>
                  <h3 className="text-sm font-bold text-muted-foreground uppercase tracking-wider mb-4">Win Rate Comparison</h3>
                  <div className="h-[200px]">
                    <ResponsiveContainer width="100%" height="100%">
                      <BarChart 
                        data={[
                          { name: player.name.split(' ')[0], winRate, fullName: player.name },
                          ...comparePlayers.map((cp) => ({
                            name: cp.name.split(' ')[0],
                            winRate: cp.wins + cp.losses > 0 ? Math.round((cp.wins / (cp.wins + cp.losses)) * 100) : 0,
                            fullName: cp.name
                          }))
                        ]}
                        margin={{ top: 5, right: 20, left: 0, bottom: 5 }}
                      >
                        <CartesianGrid strokeDasharray="3 3" stroke="#27272A" vertical={false} />
                        <XAxis dataKey="name" tick={{ fill: '#71717A', fontSize: 12 }} axisLine={{ stroke: '#27272A' }} />
                        <YAxis domain={[0, 100]} tick={{ fill: '#71717A', fontSize: 12 }} axisLine={{ stroke: '#27272A' }} tickFormatter={(v) => `${v}%`} />
                        <Tooltip 
                          contentStyle={{ backgroundColor: '#121212', border: '1px solid #27272A', borderRadius: '8px' }}
                          formatter={(value, name, props) => [`${value}%`, props.payload.fullName]}
                        />
                        <Bar dataKey="winRate" radius={[4, 4, 0, 0]}>
                          {[player, ...comparePlayers].map((_, index) => (
                            <Cell key={`cell-${index}`} fill={COMPARISON_COLORS[index]} />
                          ))}
                        </Bar>
                      </BarChart>
                    </ResponsiveContainer>
                  </div>
                </div>

                {/* Hit Rate Trend Comparison */}
                {chartData.hitRateData.length > 0 && (
                  <div>
                    <h3 className="text-sm font-bold text-muted-foreground uppercase tracking-wider mb-4">Hit Rate Trend Comparison</h3>
                    <div className="h-[250px]">
                      <ResponsiveContainer width="100%" height="100%">
                        <RechartsLineChart margin={{ top: 5, right: 20, left: 0, bottom: 5 }}>
                          <CartesianGrid strokeDasharray="3 3" stroke="#27272A" />
                          <XAxis 
                            dataKey="pick" 
                            tick={{ fill: '#71717A', fontSize: 12 }} 
                            axisLine={{ stroke: '#27272A' }}
                            allowDuplicatedCategory={false}
                          />
                          <YAxis 
                            domain={[0, 100]} 
                            tick={{ fill: '#71717A', fontSize: 12 }} 
                            axisLine={{ stroke: '#27272A' }}
                            tickFormatter={(v) => `${v}%`}
                          />
                          <Tooltip 
                            contentStyle={{ backgroundColor: '#121212', border: '1px solid #27272A', borderRadius: '8px' }}
                            formatter={(value, name) => [`${value}%`, name]}
                          />
                          <Legend />
                          {/* Main player line */}
                          <Line 
                            data={chartData.hitRateData}
                            type="monotone" 
                            dataKey="rate" 
                            name={player.name}
                            stroke={COMPARISON_COLORS[0]}
                            strokeWidth={2}
                            dot={{ fill: COMPARISON_COLORS[0], strokeWidth: 0, r: 3 }}
                          />
                          {/* Compare players lines */}
                          {comparePlayers.map((cp, idx) => {
                            const cpPicks = cp.picks?.filter(p => p.outcome && ['WIN', 'LOSS', 'PUSH'].includes(p.outcome.toUpperCase())).reverse() || [];
                            const cpHitRateData = [];
                            let runningWins = 0;
                            cpPicks.forEach((pick, i) => {
                              if (pick.outcome?.toUpperCase() === 'WIN') runningWins++;
                              cpHitRateData.push({
                                pick: i + 1,
                                rate: Math.round((runningWins / (i + 1)) * 100)
                              });
                            });
                            return (
                              <Line 
                                key={cp.name}
                                data={cpHitRateData}
                                type="monotone" 
                                dataKey="rate" 
                                name={cp.name}
                                stroke={COMPARISON_COLORS[idx + 1]}
                                strokeWidth={2}
                                dot={{ fill: COMPARISON_COLORS[idx + 1], strokeWidth: 0, r: 3 }}
                              />
                            );
                          })}
                        </RechartsLineChart>
                      </ResponsiveContainer>
                    </div>
                  </div>
                )}
              </div>
            )}
          </div>
        )}

        {/* Custom Content Section - Editable by Admin */}
        <div className="bg-card border border-border rounded-lg p-6 mb-12">
          <div className="flex justify-between items-center mb-4">
            <h2 className="text-xl font-heading font-bold">Player Analysis</h2>
            {isAuthenticated && !editing && (
              <Button variant="outline" size="sm" onClick={() => setEditing(true)}>
                <Edit2 size={14} className="mr-1" /> Edit
              </Button>
            )}
            {editing && (
              <div className="flex gap-2">
                <Button variant="outline" size="sm" onClick={() => setEditing(false)}>
                  <X size={14} className="mr-1" /> Cancel
                </Button>
                <Button size="sm" onClick={saveCustomContent} className="bg-primary text-white">
                  <Save size={14} className="mr-1" /> Save
                </Button>
              </div>
            )}
          </div>

          {editing ? (
            <Textarea
              value={customContent}
              onChange={(e) => setCustomContent(e.target.value)}
              placeholder="Add custom analysis, notes, or content about this player..."
              className="min-h-[200px] font-mono text-sm"
            />
          ) : (
            <div className="prose prose-invert max-w-none">
              {player.custom_content ? (
                <p className="text-foreground whitespace-pre-wrap">{player.custom_content}</p>
              ) : (
                <p className="text-muted-foreground italic">
                  No custom analysis added yet. {isAuthenticated && 'Click Edit to add content.'}
                </p>
              )}
            </div>
          )}
        </div>

        {/* Pick History */}
        <div>
          <h2 className="text-2xl font-heading font-bold mb-6">Pick History & Intelligence Reports</h2>
          
          {player.picks && player.picks.length > 0 ? (
            <div className="space-y-6">
              {player.picks.map((pick, index) => {
                const outcomeNormalized = (pick.outcome || '').toUpperCase();
                const isCompleted = outcomeNormalized && outcomeNormalized !== 'PENDING';
                
                return (
                  <div 
                    key={pick.id || index}
                    className="bg-card border border-border rounded-lg overflow-hidden"
                  >
                    {/* Header */}
                    <div className="p-4 border-b border-border/50 flex flex-col md:flex-row md:items-center md:justify-between gap-4">
                      <div className="flex items-center gap-4">
                        {getOutcomeIcon(outcomeNormalized)}
                        <div>
                          <div className="font-bold text-lg">
                            {pick.pick} {pick.line} {pick.prop_type}
                          </div>
                          <div className="text-sm text-muted-foreground flex items-center gap-2 flex-wrap">
                            <Calendar size={14} />
                            {formatDate(pick.pick_date || pick.created_at)}
                            {pick.game && <span className="ml-2">• {pick.game}</span>}
                            {pick.opponent && <span>vs {pick.opponent}</span>}
                          </div>
                        </div>
                      </div>
                      
                      <div className="flex items-center gap-4">
                        {pick.actual_stat !== null && pick.actual_stat !== undefined && (
                          <div className="text-sm bg-surface px-3 py-2 rounded">
                            <span className="text-muted-foreground">Line: </span>
                            <span className="font-mono">{pick.line}</span>
                            <span className="mx-2 text-muted-foreground">→</span>
                            <span className="text-muted-foreground">Actual: </span>
                            <span className={`font-bold font-mono ${
                              outcomeNormalized === 'WIN' ? 'text-green-500' : 
                              outcomeNormalized === 'LOSS' ? 'text-red-500' : ''
                            }`}>
                              {pick.actual_stat}
                            </span>
                          </div>
                        )}
                        {pick.grade && (
                          <div className="text-sm bg-primary/10 text-primary px-3 py-1 rounded font-bold">
                            Grade: {pick.grade}
                          </div>
                        )}
                        {pick.psl_prop_score && (
                          <div className="text-sm bg-surface px-3 py-1 rounded font-mono">
                            Score: {pick.psl_prop_score}/100
                          </div>
                        )}
                        <div className={`px-3 py-1 rounded text-sm font-bold ${
                          outcomeNormalized === 'WIN' 
                            ? 'bg-green-500/20 text-green-500' 
                            : outcomeNormalized === 'LOSS'
                            ? 'bg-red-500/20 text-red-500'
                            : 'bg-yellow-500/20 text-yellow-500'
                        }`}>
                          {outcomeNormalized || 'PENDING'}
                        </div>
                      </div>
                    </div>
                    
                    {/* Full Intelligence Report - Only shown after game completes */}
                    {isCompleted && (
                      <div className="p-4 space-y-6">
                        {/* Reasoning Summary */}
                        {pick.reasoning && (
                          <div>
                            <h4 className="text-xs font-mono uppercase tracking-widest text-muted-foreground mb-2">Analysis Summary</h4>
                            <p className="text-sm">{pick.reasoning}</p>
                          </div>
                        )}
                        
                        {/* Projection Data Grid */}
                        {(pick.last_5_avg || pick.season_avg || pick.season_median) && (
                          <div>
                            <h4 className="text-xs font-mono uppercase tracking-widest text-muted-foreground mb-3">Outlier Projection Data</h4>
                            <div className="grid grid-cols-2 md:grid-cols-5 gap-3">
                              {pick.last_5_avg && (
                                <div className="bg-surface p-3 rounded">
                                  <div className="text-xs text-muted-foreground">L5 Avg</div>
                                  <div className="font-bold font-mono">{pick.last_5_avg}</div>
                                  {pick.l5_gap && <div className="text-xs text-green-500">+{pick.l5_gap}</div>}
                                </div>
                              )}
                              {pick.last_10_avg && (
                                <div className="bg-surface p-3 rounded">
                                  <div className="text-xs text-muted-foreground">L10 Avg</div>
                                  <div className="font-bold font-mono">{pick.last_10_avg}</div>
                                  {pick.l10_gap && <div className="text-xs text-green-500">+{pick.l10_gap}</div>}
                                </div>
                              )}
                              {pick.last_20_avg && (
                                <div className="bg-surface p-3 rounded">
                                  <div className="text-xs text-muted-foreground">L20 Avg</div>
                                  <div className="font-bold font-mono">{pick.last_20_avg}</div>
                                  {pick.l20_gap && <div className="text-xs text-green-500">+{pick.l20_gap}</div>}
                                </div>
                              )}
                              {pick.season_avg && (
                                <div className="bg-surface p-3 rounded">
                                  <div className="text-xs text-muted-foreground">Season Avg</div>
                                  <div className="font-bold font-mono">{pick.season_avg}</div>
                                </div>
                              )}
                              {pick.season_median && (
                                <div className="bg-surface p-3 rounded">
                                  <div className="text-xs text-muted-foreground">Season Median</div>
                                  <div className="font-bold font-mono">{pick.season_median}</div>
                                  {pick.season_median_gap && <div className="text-xs text-green-500">+{pick.season_median_gap}</div>}
                                </div>
                              )}
                            </div>
                          </div>
                        )}
                        
                        {/* Structural Edge */}
                        {pick.structural_edge_class && (
                          <div>
                            <h4 className="text-xs font-mono uppercase tracking-widest text-muted-foreground mb-2">Structural Edge</h4>
                            <div className="flex items-center gap-2 mb-2">
                              <span className="bg-green-500/20 text-green-500 px-2 py-1 rounded text-sm font-bold">{pick.structural_edge_class}</span>
                            </div>
                            {pick.structural_cushion && <p className="text-sm text-muted-foreground">{pick.structural_cushion}</p>}
                          </div>
                        )}
                        
                        {/* Hit Rates */}
                        {(pick.last_5_hit_rate || pick.last_10_hit_rate || pick.season_hit_rate) && (
                          <div>
                            <h4 className="text-xs font-mono uppercase tracking-widest text-muted-foreground mb-3">Trend Data (Hit Rates)</h4>
                            <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
                              {pick.last_5_hit_rate && (
                                <div className="bg-surface p-3 rounded">
                                  <div className="text-xs text-muted-foreground">Last 5</div>
                                  <div className="font-bold text-primary">{pick.last_5_hit_rate}</div>
                                </div>
                              )}
                              {pick.last_10_hit_rate && (
                                <div className="bg-surface p-3 rounded">
                                  <div className="text-xs text-muted-foreground">Last 10</div>
                                  <div className="font-bold text-primary">{pick.last_10_hit_rate}</div>
                                </div>
                              )}
                              {pick.last_20_hit_rate && (
                                <div className="bg-surface p-3 rounded">
                                  <div className="text-xs text-muted-foreground">Last 20</div>
                                  <div className="font-bold text-primary">{pick.last_20_hit_rate}</div>
                                </div>
                              )}
                              {pick.season_hit_rate && (
                                <div className="bg-surface p-3 rounded">
                                  <div className="text-xs text-muted-foreground">Season</div>
                                  <div className="font-bold text-primary">{pick.season_hit_rate}</div>
                                </div>
                              )}
                            </div>
                          </div>
                        )}
                        
                        {/* Head-to-Head */}
                        {pick.h2h_hit_rate && (
                          <div>
                            <h4 className="text-xs font-mono uppercase tracking-widest text-muted-foreground mb-2">Head-to-Head vs {pick.opponent || 'Opponent'}</h4>
                            <div className="flex items-center gap-4 mb-2">
                              <span className="font-bold">{pick.h2h_hit_rate}</span>
                              {pick.h2h_avg && <span className="text-sm text-muted-foreground">Avg: {pick.h2h_avg}</span>}
                              {pick.h2h_median && <span className="text-sm text-muted-foreground">Med: {pick.h2h_median}</span>}
                            </div>
                            {pick.h2h_outcomes && <div className="text-xs text-muted-foreground">Outcomes: {pick.h2h_outcomes}</div>}
                            {pick.h2h_analysis && <p className="text-sm text-muted-foreground mt-2">{pick.h2h_analysis}</p>}
                          </div>
                        )}
                        
                        {/* Matchup Context */}
                        {pick.matchup_interpretation && (
                          <div>
                            <h4 className="text-xs font-mono uppercase tracking-widest text-muted-foreground mb-2">Matchup Context</h4>
                            <p className="text-sm">{pick.matchup_interpretation}</p>
                            {(pick.opp_pts_allowed || pick.opp_pace_rank) && (
                              <div className="flex flex-wrap gap-3 mt-2">
                                {pick.opp_pts_allowed && <span className="text-xs bg-surface px-2 py-1 rounded">Pts Allowed: {pick.opp_pts_allowed} (Rank {pick.opp_pts_rank})</span>}
                                {pick.opp_reb_allowed && <span className="text-xs bg-surface px-2 py-1 rounded">Reb Allowed: {pick.opp_reb_allowed} (Rank {pick.opp_reb_rank})</span>}
                                {pick.opp_ast_allowed && <span className="text-xs bg-surface px-2 py-1 rounded">Ast Allowed: {pick.opp_ast_allowed} (Rank {pick.opp_ast_rank})</span>}
                                {pick.opp_pace_rank && <span className="text-xs bg-surface px-2 py-1 rounded">Pace: {pick.opp_pace_rank}</span>}
                              </div>
                            )}
                          </div>
                        )}
                        
                        {/* Edge Signals */}
                        {pick.edge_signals && (
                          <div>
                            <h4 className="text-xs font-mono uppercase tracking-widest text-muted-foreground mb-2">Key Edge Signals</h4>
                            <p className="text-sm">{pick.edge_signals}</p>
                          </div>
                        )}
                        
                        {/* Risk & Kill Switch */}
                        {(pick.risk_conditions || pick.kill_switch) && (
                          <div className="bg-red-500/5 border border-red-500/20 rounded p-3">
                            <h4 className="text-xs font-mono uppercase tracking-widest text-red-500/70 mb-2">Risk Assessment</h4>
                            {pick.risk_conditions && <p className="text-sm mb-2"><strong>Conditions:</strong> {pick.risk_conditions}</p>}
                            {pick.kill_switch && <p className="text-sm text-red-500/80"><strong>Kill Switch:</strong> {pick.kill_switch}</p>}
                          </div>
                        )}
                        
                        {/* Decision Card Footer */}
                        {(pick.action_reason || pick.ceiling_potential) && (
                          <div className="pt-4 border-t border-border/50">
                            <div className="grid grid-cols-1 md:grid-cols-2 gap-4 text-sm">
                              {pick.action_reason && (
                                <div>
                                  <span className="text-muted-foreground">Action Reason:</span>
                                  <p>{pick.action_reason}</p>
                                </div>
                              )}
                              {pick.ceiling_potential && (
                                <div>
                                  <span className="text-muted-foreground">Ceiling Potential:</span>
                                  <p>{pick.ceiling_potential}</p>
                                </div>
                              )}
                            </div>
                          </div>
                        )}
                      </div>
                    )}
                    
                    {/* Pending picks show minimal info */}
                    {!isCompleted && pick.heat_level && (
                      <div className="p-4 bg-surface/50">
                        <div className="flex items-center gap-2">
                          <span className="text-xs text-muted-foreground">Heat Level:</span>
                          <span className="text-sm font-bold text-primary">{pick.heat_level}</span>
                          {pick.psl_prop_score && <span className="text-xs text-muted-foreground ml-4">Score: {pick.psl_prop_score}/100</span>}
                        </div>
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
          ) : (
            <div className="text-center py-12 bg-card border border-border rounded-lg">
              <Target className="mx-auto text-muted-foreground mb-4" size={40} />
              <p className="text-muted-foreground">No picks recorded yet</p>
            </div>
          )}
        </div>

        {/* Internal Linking for SEO */}
        <div className="mt-16 pt-8 border-t border-border">
          <h3 className="text-lg font-heading font-bold mb-4">More Player Analysis</h3>
          <Link to="/players" className="text-primary hover:underline">
            ← View All Players
          </Link>
        </div>
      </div>
    </Layout>
  );
}
